import { useEffect, useId, useRef } from "react";
import { Chips, ChipsProps } from "primereact/chips";
import { IconOptions } from "primereact/utils";
import { Icon } from "../Icon";

import styles from "./MultiInput.module.css";
import inputStyles from "../Inputs/Input.module.css";

export interface MultiCodeInputProps {
  label?: string;
  placeholder?: string;
  values?: string[];
  setValues: (values: string[]) => void;
  validationError?: string;
  className?: string;
  wrapperStyles?: React.CSSProperties;
  allowDuplicate?: boolean;
  maxValues?: number;
  required?: boolean;
  description?: string;
}

const chipItemTemplate = (item: string) => (
  <span className={styles.name}>{item}</span>
);

const chipRemoveIcon = (options: IconOptions<ChipsProps>) => (
  <button
    type="button"
    className={styles.deleteIcon}
    onClick={options.iconProps.onClick as React.MouseEventHandler}
    aria-label="Remove item"
  >
    <Icon
      name="x"
      color="Grey-400"
      size={18}
      hoverEffect
      ariaLabel="Remove item"
    />
  </button>
);

export default function MultiStringInput({
  label,
  placeholder,
  values = [],
  setValues,
  validationError,
  className,
  allowDuplicate = false,
  maxValues,
  required,
  description,
}: Readonly<MultiCodeInputProps>) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputId = useId();
  const labelId = `${inputId}-label`;
  const descriptionId = description ? `${inputId}-description` : undefined;
  const errorId = validationError ? `${inputId}-error` : undefined;
  const listId = `${inputId}-list`;

  useEffect(() => {
    if (!wrapperRef.current) return;

    const chipsContainer = wrapperRef.current.querySelector(".p-chips");
    if (!chipsContainer) return;

    const ulElement = chipsContainer.querySelector("ul");
    if (!ulElement) return;

    const setAccessibilityAttributes = () => {
      ulElement.removeAttribute("role");
      ulElement.removeAttribute("aria-orientation");

      ulElement.setAttribute("role", "list");
      ulElement.id = listId;

      if (label) {
        ulElement.setAttribute("aria-labelledby", labelId);
        ulElement.removeAttribute("aria-label");
      } else {
        ulElement.setAttribute("aria-label", "Selected items");
        ulElement.removeAttribute("aria-labelledby");
      }

      const listItems = Array.from(ulElement.children).filter(
        (child) => child.tagName.toLowerCase() === "li",
      ) as HTMLLIElement[];

      listItems.forEach((li) => {
        li.removeAttribute("role");
        li.removeAttribute("aria-selected");
        li.setAttribute("role", "listitem");
      });
    };

    const setInputAccessibilityAttributes = () => {
      const input = chipsContainer.querySelector("input");
      if (input) {
        input.id = inputId;
        if (label) {
          input.setAttribute("aria-labelledby", labelId);
        } else {
          input.setAttribute("aria-label", "Multi-string input");
        }

        const describedBy = [descriptionId, errorId].filter(Boolean).join(" ");
        if (describedBy) {
          input.setAttribute("aria-describedby", describedBy);
        } else {
          input.removeAttribute("aria-describedby");
        }

        if (required) {
          input.setAttribute("aria-required", "true");
        } else {
          input.removeAttribute("aria-required");
        }
      }
    };

    const timeoutId = setTimeout(() => {
      setAccessibilityAttributes();
      setInputAccessibilityAttributes();
    }, 0);

    const observer = new MutationObserver(() => {
      setAccessibilityAttributes();
      setInputAccessibilityAttributes();
    });

    observer.observe(ulElement, {
      childList: true,
      subtree: false,
    });

    observer.observe(chipsContainer, {
      childList: true,
      subtree: true,
    });

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [label, labelId, inputId, listId, descriptionId, errorId, required]);

  return (
    <div ref={wrapperRef} className={styles.multiStringWrapper}>
      {label && (
        <label id={labelId} htmlFor={inputId} className="strong">
          {required && <span className={inputStyles.requiredIcon}>*</span>}
          {label}
        </label>
      )}
      {!label && required && (
        <span className={inputStyles.requiredIcon} aria-hidden="true">
          *
        </span>
      )}
      <Chips
        inputId={inputId}
        placeholder={values.length === 0 ? placeholder : undefined}
        value={values.length === 0 ? undefined : values}
        onChange={(e) => {
          const valuesToSet =
            e.value?.filter((value) => value.length && !value.includes(",")) ||
            [];
          setValues(maxValues ? valuesToSet.slice(0, maxValues) : valuesToSet);
        }}
        addOnBlur
        max={maxValues}
        itemTemplate={chipItemTemplate}
        removeIcon={chipRemoveIcon}
        allowDuplicate={allowDuplicate}
        separator=","
        className={className ?? ""}
      />
      {description && (
        <p id={descriptionId} className="description">
          {description}
        </p>
      )}
      {validationError && (
        <div id={errorId} className={styles.validationError} role="alert">
          {validationError}
        </div>
      )}
    </div>
  );
}
